import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sparkles,
    MessageSquare,
    GitBranch,
    Calendar,
    BarChart3,
    Palette,
    Check,
    Zap,
    ArrowRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import LandingNavigation from '../components/LandingNavigation';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const features = [
    {
        icon: Sparkles,
        title: 'AI Creative Generation',
        description: 'Generate stunning ad creatives in seconds using advanced AI. Simply describe your vision, and watch as professional-quality visuals come to life.',
        benefits: [
            'Generate unlimited creative variations',
            'Smart prompt builder for better results',
            'Refine and iterate with AI assistance',
        ],
        screenshot: '/screenshots/creative-generation.png',
    },
    {
        icon: MessageSquare,
        title: 'Conversational AI',
        description: 'Chat naturally with AI to brainstorm, refine, and perfect your creative concepts. Get instant feedback and suggestions tailored to your brand.',
        benefits: [
            'Natural language creative direction',
            'Context-aware suggestions',
            'Save and revisit conversations',
        ],
        screenshot: '/screenshots/conversational.png',
    },
    {
        icon: GitBranch,
        title: 'Visual Funnel Builder',
        description: 'Build complete advertising funnels with an intuitive drag-and-drop canvas. Connect campaigns, ad sets, and creatives visually.',
        benefits: [
            'Drag-and-drop campaign structure',
            'A/B testing built-in',
            'Retargeting pool management',
        ],
        screenshot: '/screenshots/funnel-builder.png',
    },
    {
        icon: Calendar,
        title: 'Content Scheduler',
        description: 'Plan and schedule your content calendar with ease. Visualize your publishing schedule and ensure consistent coverage across all platforms.',
        benefits: [
            'Visual calendar interface',
            'Multi-platform scheduling',
            'Coverage gap detection',
        ],
        screenshot: '/screenshots/scheduler.png',
    },
    {
        icon: BarChart3,
        title: 'Analytics & AI Insights',
        description: 'Get AI-powered insights to optimize your campaigns. Understand what works, identify opportunities, and make data-driven decisions.',
        benefits: [
            'Real-time performance metrics',
            'AI-powered recommendations',
            'ROAS tracking and optimization',
        ],
        screenshot: '/screenshots/analytics.png',
    },
    {
        icon: Palette,
        title: 'Multi-Brand Management',
        description: 'Manage multiple brands from a single workspace. Keep brand assets, guidelines, and campaigns organized and easily accessible.',
        benefits: [
            'Centralized brand management',
            'Brand-specific asset libraries',
            'Quick brand switching',
        ],
        screenshot: '/screenshots/brands.png',
    },
];

const platforms = [
    { name: 'Instagram', icon: '📸' },
    { name: 'Facebook', icon: '👤' },
    { name: 'LinkedIn', icon: '💼' },
    { name: 'TikTok', icon: '🎵' },
    { name: 'Google Ads', icon: '🔍' },
];

const FeatureScreenshot = ({ src, alt, icon: Icon }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return (
            <div className="landing-feature-screenshot-placeholder">
                <Icon size={64} strokeWidth={1.5} />
                <span>Screenshot coming soon</span>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            onError={() => setHasError(true)}
            style={{ width: '100%', height: 'auto', display: 'block' }}
        />
    );
};

const LandingPage = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            navigate('/dashboard');
        }
    }, [currentUser, navigate]);

    const handleGetStarted = () => {
        navigate('/login');
    };

    if (currentUser) {
        return null;
    }

    return (
        <div className="landing-page">
            <LandingNavigation />

            {/* Hero Section */}
            <section className="landing-hero">
                <div className="landing-hero-content">
                    <div className="landing-hero-badge">
                        <Zap size={16} />
                        AI-Powered Marketing Platform
                    </div>
                    <h1 className="landing-hero-title">
                        Create Stunning Ad Creatives{' '}
                        <span className="landing-hero-title-gradient">
                            Powered by AI
                        </span>
                    </h1>
                    <p className="landing-hero-subtitle">
                        Generate professional ad creatives, build marketing funnels,
                        and optimize campaigns across all platforms — all from one powerful workspace.
                    </p>
                    <div className="landing-hero-actions">
                        <button
                            onClick={handleGetStarted}
                            className="btn btn-primary"
                        >
                            Get Started Free
                            <ArrowRight size={20} />
                        </button>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="btn btn-secondary"
                        >
                            View Pricing
                        </button>
                    </div>
                </div>
            </section>

            {/* Platform Integrations */}
            <section className="landing-platforms">
                <p className="landing-platforms-title">
                    Connect all your ad platforms in one place
                </p>
                <div className="landing-platforms-grid">
                    {platforms.map((platform) => (
                        <div key={platform.name} className="landing-platform-item">
                            <span style={{ fontSize: '2rem' }}>{platform.icon}</span>
                            <span>{platform.name}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section id="features" className="landing-features">
                {features.map((feature, index) => (
                    <div key={feature.title} className="landing-feature-section">
                        <div className="landing-feature-content">
                            <div className="landing-feature-icon">
                                <feature.icon size={28} />
                            </div>
                            <h2 className="landing-feature-title">{feature.title}</h2>
                            <p className="landing-feature-description">
                                {feature.description}
                            </p>
                            <ul className="landing-feature-list">
                                {feature.benefits.map((benefit) => (
                                    <li key={benefit}>
                                        <Check size={18} />
                                        {benefit}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="landing-feature-visual">
                            <div className="landing-feature-screenshot">
                                <FeatureScreenshot
                                    src={feature.screenshot}
                                    alt={feature.title}
                                    icon={feature.icon}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* CTA Section */}
            <section className="landing-cta">
                <div className="landing-cta-content">
                    <h2 className="landing-cta-title">
                        Ready to Transform Your Creative Workflow?
                    </h2>
                    <p className="landing-cta-subtitle">
                        Join thousands of marketers using AI to create better ads faster.
                    </p>
                    <button onClick={handleGetStarted} className="btn">
                        Get Started Free
                        <ArrowRight size={20} />
                    </button>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default LandingPage;
